import React from 'react';
import type {FileCheck} from '../lib/validate';

/**
 * Renders the result of a client-side leader-file check (see `lib/validate`). Green summary with
 * entity counts on success; a red, scannable list of problems on failure. Presentational only —
 * the caller owns running the check and gating any action on `check.status`.
 */

const MAX_ERRORS_SHOWN = 20;

export function ValidationReport({
  check,
  fileName
}: {
  check: FileCheck | null;
  fileName?: string;
}): React.ReactElement | null {
  if (!check) return null;

  if (check.status === 'valid' && check.summary) {
    const s = check.summary;
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-4" role="status" aria-live="polite">
        <div className="flex items-center gap-2 text-green-800">
          <CheckIcon />
          <span className="text-sm font-semibold">File looks valid{fileName ? ` — ${fileName}` : ''}</span>
        </div>
        <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1.5 text-xs text-green-900">
          <Stat label="Domains" value={s.domains} />
          <Stat label="Jobs-to-be-done" value={s.jtbds} />
          <Stat label="Service areas" value={s.serviceAreas} />
          <Stat label="Phases" value={s.phases} />
          <Stat label="Size" value={`${(s.bytes / 1024).toFixed(1)} KB`} />
        </dl>
      </div>
    );
  }

  const shown = check.errors.slice(0, MAX_ERRORS_SHOWN);
  const overflow = check.errors.length - shown.length;
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4" role="alert">
      <div className="flex items-center gap-2 text-red-800">
        <WarningIcon />
        <span className="text-sm font-semibold">
          {check.errors.length} {check.errors.length === 1 ? 'issue' : 'issues'} found
          {fileName ? ` in ${fileName}` : ''}
        </span>
      </div>
      <ul className="mt-2.5 list-disc space-y-1 pl-5 text-xs text-red-900">
        {shown.map((e, i) => (
          <li key={i}>{e}</li>
        ))}
      </ul>
      {overflow > 0 && <p className="mt-2 text-xs text-red-700">…and {overflow} more.</p>}
    </div>
  );
}

function Stat({label, value}: {label: string; value: React.ReactNode}): React.ReactElement {
  return (
    <div className="flex items-baseline gap-1.5">
      <dt className="text-green-700">{label}</dt>
      <dd className="font-semibold tabular-nums">{value}</dd>
    </div>
  );
}

function CheckIcon(): React.ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
    </svg>
  );
}

function WarningIcon(): React.ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
    </svg>
  );
}
